/**
 * One tab for the vendor, one for us (Andrew, 2026-09-24).
 *
 * a28i keeps its own tab and SABA/WeGa gets exactly one of its own: the first launch opens it, and every
 * later launch - the hall (New Hall) or a single game - is steered into that same tab instead of piling up
 * a second one. Inside the vendor's own hall, moving between games is SABA's own navigation and stays in
 * that tab.
 *
 * The tab is claimed BEFORE the request goes out: a window opened after an await is blocked as a popup, so
 * the click handler opens it synchronously and we navigate it once the launch URL comes back.
 *
 * `window.open` with a NAME reuses a tab that already carries that name, and it does not navigate it while
 * no URL is passed - which is what makes "the same tab" work even after our own page has been reloaded and
 * the handle below is gone.
 */

const TARGET = 'wega';

// The handle, for the life of this page load. A window handle stays usable after it has navigated
// cross-origin (location assignment, focus and `closed` all keep working), so it is kept, not dropped.
let wegaTab = null;

// about:blank is same-origin and readable; the vendor's page is cross-origin and throws on read.
const isBlank = (tab) => {
  try {
    return tab.location.href === 'about:blank';
  } catch (err) {
    return false;
  }
};

/**
 * Claims the WeGa tab. Call this synchronously from the click handler, before the launch request.
 * @returns {Window|null} The tab to hand to `sendWeGaTab` / `releaseWeGaTab`, null if the browser refused
 *                        to open one (popup blocked).
 */
export const claimWeGaTab = () => {
  if (wegaTab && !wegaTab.closed) {
    // Ours already: nothing to pre-open, and a later location assignment is not a popup.
    return wegaTab;
  }
  // Opened from the click, so the browser keeps it; a name that is already in use reuses that tab as-is.
  wegaTab = window.open('', TARGET);
  return wegaTab;
};

/**
 * Sends the player to `url` in the claimed tab.
 * If the player closed that tab while the request was in flight, the launch would be lost, so it falls back
 * to this tab rather than dropping it.
 */
export const sendWeGaTab = (tab, url) => {
  if (tab && !tab.closed) {
    tab.location.href = url;
    tab.focus();
    return;
  }
  window.location.href = url;
};

/**
 * Gives up a claimed tab that will not be used (the launch answered no URL, or the request failed).
 * A blank tab we opened ourselves is closed; a tab already showing the vendor's page is never taken away.
 */
export const releaseWeGaTab = (tab) => {
  if (!tab || tab.closed) return;
  if (isBlank(tab)) tab.close();
};
